/* eslint-disable i18next/no-literal-string */
import * as React from "react";
import { Link } from "~/components/ui/link";
import { cn } from "~/lib/utils";
import { colors } from "~/components/landing/colors";
import {
  useAddToEmbeddedCart,
  useEmbeddedCartCatalog,
} from "~/lib/use-embedded-cart";
import { Picture } from "~/components/ui/picture";
import { imageUrl } from "~/lib/image-url";

// Plain public/ string paths, not JS imports — see the comment on bgImageSet in
// app/components/home/UseCases.tsx for why.
const bouncieDeviceInBox = imageUrl("/images/landing/Bouncie-OBD-Box-relit.png");

/**
 * Ported from bouncieweb's `LandingPageComponents/Pricing.js`.
 *
 * Faithful reproduction of the original Emotion-styled pricing block plus its
 * `AddToCartButton.js` and `PriceWithSuperscriptsAndSubscript.js` dependencies.
 *
 * Deviations from the original (documented, both tied to infrastructure that
 * does not exist in this project):
 *   - The Amazon "Order on Amazon" CTA (`AmazonOrderCTA`) and the Prime Day
 *     banner (`AmazonPrimeDayBanner`) are omitted. Both read Redux `config`
 *     state (amazonPrimeDay / amazonPrimeBigDealDays) that this app has no
 *     store for, so they cannot be reproduced faithfully.
 *   - `emphasizeBulkDiscount` defaults to false as before and no ported page
 *     sets it, so the bulk-discount emphasis branch is inert in this port.
 *   - The original's "Build a Quote" button links to `/quote`. This project's
 *     equivalent route is `fleet/quote`, so `showQuoteButton` links there.
 *
 * Original breakpoint map (theme/mediaQueries):
 *   xsm 431px -> min-[431px]:  md 768px -> md:  lg 1024px -> lg:  xlg 1280px -> xl:
 */

// --- PriceWithSuperscriptsAndSubscript (ported inline) --------------------

// integer part of the price with thousands separators; undefined until price>0.
function derivePriceNoZero(price?: number): string | undefined {
  if (price === undefined || price <= 0) return undefined;
  const whole = Math.trunc(price).toString();
  return whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

// two-digit cents ("00" for whole numbers); undefined until price>0.
function deriveCentsNoZero(price?: number): string | undefined {
  if (price === undefined || price <= 0) return undefined;
  if (Number.isInteger(price)) return "00";
  const cents = price.toString().split(".")[1] ?? "";
  return (cents + "0").slice(0, 2);
}

function PriceWithSuperscriptsAndSubscript({
  price,
  currencySymbol,
  currencyCode,
  className,
}: {
  price?: number;
  currencySymbol?: string;
  currencyCode?: string;
  className?: string;
}) {
  // Original hides the container while price is falsy (loading). We render
  // nothing so the layout collapses gracefully until the cart catalog loads.
  if (!price) return null;

  return (
    <div
      className={cn(
        "font-display relative inline-flex items-start font-bold leading-[0.95]",
        className,
      )}
      style={{ color: colors.zachBlack }}
    >
      <div className="text-[0.4em]">{currencySymbol}</div>
      <div className="min-h-[1em] px-[3px] leading-[0.95]">
        {derivePriceNoZero(price)}
      </div>
      <div>
        <div className="text-[0.4em]">{deriveCentsNoZero(price)}</div>
        {currencyCode && <div className="text-[0.2em]">{currencyCode}</div>}
      </div>
    </div>
  );
}

// --- AddToCartButton (ported inline) --------------------------------------

export function AddToCartButton({
  className,
  copy = "Add to Cart",
  ...props
}: Omit<React.ComponentProps<"button">, "children"> & {
  copy?: string;
}) {
  const addToCart = useAddToEmbeddedCart("bouncieobd");
  return (
    <button
      type="button"
      onClick={addToCart}
      className={cn(
        "inline-flex w-[70%] cursor-pointer justify-center rounded-[10px] border-0 p-3",
        "text-[18px] text-white transition-colors duration-150 md:w-full",
        "bg-[#1F69F2] hover:bg-[#0954E0] hover:no-underline",
        className,
      )}
      {...props}
    >
      {copy}
    </button>
  );
}

// --- BuildQuoteButton (ported inline) -------------------------------------

// Outlined counterpart to AddToCartButton. Original targeted `/quote`.
function BuildQuoteButton({ children, ...props }: Omit<React.ComponentProps<typeof Link>, "to">) {
  return (
    <Link
      to="/fleet/quote"
      className={cn(
        "trigger-gtm-pricing-build-a-quote",
        "mt-4 inline-flex w-[70%] cursor-pointer justify-center rounded-[10px] p-3",
        "border-2 border-solid bg-transparent text-[18px] md:w-full",
        "transition-[background] duration-250",
        "border-[#1F69F2] text-[#1F69F2]",
        "hover:border-[#0954E0] hover:bg-[#0954E0] hover:text-white hover:no-underline",
      )}
      {...props}
    >
      {children}
    </Link>
  );
}

// --- Pricing ---------------------------------------------------------------

export default function Pricing({
  className,
  showQuoteButton = false,
  quoteButtonText = "Build a Quote",
  children,
}: {
  className?: string;
  showQuoteButton?: boolean;
  quoteButtonText?: string;
  /**
   * Rendered at the bottom of the blue subscription box, matching where the
   * original placed `{children}` (inside SubscriptionWrapper, after the
   * subscription row). /shoploaner uses it for the ShopLoaner fee disclaimer.
   */
  children?: React.ReactNode;
} = {}) {
  const {
    unitPrice: deviceUnitPrice,
    subscriptionUnitPrice,
    subscriptionAdjustedUnitPrice,
    currencySymbol,
    currencyCode,
  } = useEmbeddedCartCatalog("bouncieobd");

  return (
    <div className={cn("mt-20", className)}>
      {/* PricingLayout */}
      <div className="mt-8 flex flex-col items-center justify-center gap-4 md:flex-row md:gap-2">
        {/* DeviceImage */}
        <Picture
          src={bouncieDeviceInBox}
          alt="Bouncie device in box"
          className={cn(
            "relative h-fit w-full max-w-none",
            "min-[431px]:w-[70%] md:w-[17rem] lg:w-[23rem] xl:w-[25rem]",
          )}
        />

        {/* PricingRows */}
        <div className="relative flex w-full flex-col items-center md:w-[28rem] md:items-start">
          {/* CustomH2 (BentoGridH2Large) */}
          <h2
            className={cn(
              "font-display relative pr-0 pb-4 font-bold leading-[1.15em]",
              "text-[9vw] min-[431px]:text-[6vw] sm:text-[33px] lg:text-[40px] xl:text-[40px]",
            )}
          >
            Simple Pricing
          </h2>

          {/* PricingRow — Bouncie Device */}
          <div className="mb-4 flex w-full items-center justify-between gap-2">
            <div>
              <h3 className="font-display pr-4 text-[24px] font-bold">
                Bouncie Device
              </h3>
              <p className="m-0 text-[14px]">
                OBD2&nbsp;| Telematics&nbsp;| GPS&nbsp;| 4G&nbsp;LTE
              </p>
            </div>
            <PriceWithSuperscriptsAndSubscript
              price={deviceUnitPrice}
              currencyCode={currencyCode}
              currencySymbol={currencySymbol}
              className="text-[40px] md:text-[50px]"
            />
          </div>

          <AddToCartButton data-sid="landing-pricing-add-to-cart" className="trigger-gtm-pricing-add-to-cart" />

          {showQuoteButton && (
            <BuildQuoteButton data-sid="landing-pricing-build-quote">{quoteButtonText}</BuildQuoteButton>
          )}

          {/* SubscriptionWrapper */}
          <div
            className="mt-6 rounded-[20px] p-6"
            style={{ background: colors.blueLight1 }}
          >
            <p className="font-display mb-3 text-[12px]">
              A subscription is required for each device.
            </p>
            {/* PricingRow (marginBottom: 0) */}
            <div className="flex w-full justify-between gap-2">
              <div>
                <h3 className="font-display pr-4 text-[24px] font-bold">
                  Monthly Subscription
                </h3>
                {/* Default (emphasizeBulkDiscount = false) branch */}
                <p
                  className="font-display mb-3 text-[12px] font-bold"
                  style={{ color: colors.blue }}
                >
                  Only <sup>{currencySymbol}</sup>
                  {subscriptionAdjustedUnitPrice?.toFixed?.(2)} per device with 3
                  or more.
                </p>
                <p className="font-display mb-0 text-[12px]">
                  Your subscription is created when Bouncie is installed and can
                  be cancelled at any time.
                </p>
              </div>
              <PriceWithSuperscriptsAndSubscript
                price={subscriptionUnitPrice}
                currencyCode={currencyCode}
                currencySymbol={currencySymbol}
                className="text-[40px] md:text-[48px]"
              />
            </div>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}
